import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {MinimalFolder} from 'constants/FolderConstants';
import {SchemalessBulkImportSupportedOligoAwareTypes} from 'constants/RegistryConstants';
import MultipleFolderSelector from 'file-browser/MultipleFolderSelector';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {BASE_FONT_PROXIMA, FONT_PROXIMA_13} from 'styles/FontConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

import DerivedColumnsTable from '../derived-columns/DerivedColumnsTable';
import * as SpreadsheetBulkImportActions from '../SpreadsheetBulkImportActions';
import {
  ColumnTransformConfiguration,
  DerivedColumn,
  DerivedColumnsData,
  FileData,
  IMPORT_TYPE,
  SelectedSchemaIds,
  TaskState,
} from '../SpreadsheetBulkImportConstants';

export interface MapDerivedColumnsModalPaneProps {
  derivedColumnsData: DerivedColumnsData;
  fileData: FileData;
  importType: IMPORT_TYPE;
  linkFolderIds: Array<string>;
  modalProps: {
    title: string;
    onRequestClose: () => void;
  };
  schemalessCreationEntityType: SchemalessBulkImportSupportedOligoAwareTypes;
  selectedSchemaIds: SelectedSchemaIds;
  tableColumnNames: Array<string>;
  // Provided by configure()
  columnTransformConfigs: Array<ColumnTransformConfiguration>;
  derivedColumnsKey: keyof Omit<DerivedColumnsData, 'state'>;
  onUpdateDerivedColumns: (derivedColumns: Array<DerivedColumn>) => void;
  onValidateSpreadsheet: () => void;
}

export interface MapDerivedColumnsModalPaneConfiguration {
  columnTransformConfigs: Array<ColumnTransformConfiguration>;
  derivedColumnsKey: keyof Omit<DerivedColumnsData, 'state'>;
  onUpdateDerivedColumns: (derivedColumns: Array<DerivedColumn>) => void;
  onValidateSpreadsheet: () => void;
}

export default class MapDerivedColumnsModalPane extends React.Component<MapDerivedColumnsModalPaneProps> {
  static configure({
    columnTransformConfigs,
    derivedColumnsKey,
    onUpdateDerivedColumns,
    onValidateSpreadsheet,
  }: MapDerivedColumnsModalPaneConfiguration): MapDerivedColumnsModalPaneConfiguration {
    return {columnTransformConfigs, derivedColumnsKey, onUpdateDerivedColumns, onValidateSpreadsheet};
  }

  _renderModalContents(): JSX.Element | null {
    switch (this.props.derivedColumnsData.state) {
      case TaskState.STARTED:
      case TaskState.FAILED:
        return (
          <div className="text-center">
            <div className="u-small-padding">
              <HorizontalThrobber />
              <p className="faded u-text-uppercase">Checking your spreadsheet for additional mappings...</p>
            </div>
          </div>
        );
      case TaskState.COMPLETED: {
        const derivedColumns = this.props.derivedColumnsData[this.props.derivedColumnsKey]!;
        const helpTexts = derivedColumns.map((derivedColumn) => {
          const matchingConfigs = this.props.columnTransformConfigs.filter(
            (config) => config.columnTransform === derivedColumn.columnTransform
          );
          assert(matchingConfigs.length === 1, 'Expected to find only one matching column transform config.');
          return (
            <p className={css(styles.paragraphText)} key={matchingConfigs[0].columnTransform}>
              {matchingConfigs[0].helpText}
            </p>
          );
        });
        return (
          <>
            <p className={css(styles.headerText)}>
              Would you like to structure the following data as part of the import?
            </p>
            <p className={css(styles.paragraphText)}>
              This data will be lost if it is not captured in this step of the import.
            </p>
            {helpTexts}
            <DerivedColumnsTable
              columns={this.props.fileData.columns}
              columnTransformConfigs={this.props.columnTransformConfigs}
              derivedColumns={derivedColumns}
              getValidSchemaFieldIds={(columnIndex: number) =>
                derivedColumns[columnIndex].compatibleSchemaFieldIds || []
              }
              importType={this.props.importType}
              onUpdateDerivedColumns={this.props.onUpdateDerivedColumns}
              selectedSchemaIds={this.props.selectedSchemaIds}
              schemalessCreationEntityType={this.props.schemalessCreationEntityType}
              tableColumnNames={this.props.tableColumnNames}
            />
            {derivedColumns.some((column) => column.schemaField?.fieldType === FieldType.BIOENTITY_LINK) && (
              <div className="form-group">
                <label>Look for linked items in:</label>
                <MultipleFolderSelector
                  onSelectFolders={(folders: Array<MinimalFolder>): void => {
                    const linkFolderIds = folders.map((folder) => folder.api_identifier);
                    SpreadsheetBulkImportActions.setImportSetting({linkFolderIds});
                  }}
                  selectedFolderIds={this.props.linkFolderIds}
                  showInventory={true}
                />
              </div>
            )}
          </>
        );
      }
      default:
        assert(false, 'Unexpected TaskState');
        return null;
    }
  }

  override render(): JSX.Element {
    if (
      this.props.derivedColumnsData.state === TaskState.FAILED ||
      (this.props.derivedColumnsData.state === TaskState.COMPLETED &&
        this.props.derivedColumnsData[this.props.derivedColumnsKey]!.length === 0)
    ) {
      this.props.onValidateSpreadsheet();
      SpreadsheetBulkImportActions.goToNextPane();
    }
    return (
      <Modal
        enableRedesign={false}
        buttons={[
          {
            key: 'back',
            text: 'Back',
            onClick: SpreadsheetBulkImportActions.goToPrevPane,
          },
          {
            key: 'next',
            text: 'Next',
            style: ButtonStyle.SECONDARY,
            onClick: () => {
              this.props.onValidateSpreadsheet();
              SpreadsheetBulkImportActions.goToNextPane();
            },
            disabled:
              this.props.derivedColumnsData.state !== TaskState.COMPLETED ||
              this.props.derivedColumnsData[this.props.derivedColumnsKey]!.some((column) => !column.type),
          },
        ]}
        {...this.props.modalProps}
      >
        {this._renderModalContents()}
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  headerText: {...FONT_PROXIMA_13, fontWeight: 'bold'},
  paragraphText: {...BASE_FONT_PROXIMA, fontSize: 11},
});
